import {
  Component,
  Input,
  forwardRef,
  inject,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ScreenIdentifierService } from 'src/app/services/screen-identifier.service';
import { UtilService } from 'src/app/services/util.service';

/**
 * A reusable component for editing Data Key fields.
 * Provides edit mode toggle with validation on save.
 */
@Component({
  selector: 'vb-data-key-editor',
  templateUrl: './data-key-editor.component.html',
  styleUrls: ['./data-key-editor.component.scss'],
  standalone: true,
  imports: [TranslateModule, ButtonModule, TooltipModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataKeyEditorComponent),
      multi: true
    }
  ]
})
export class DataKeyEditorComponent implements ControlValueAccessor, OnChanges {
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly screenIdentifierService = inject(ScreenIdentifierService);
  private readonly utilService = inject(UtilService);

  /** Reference to the input element for focusing */
  @ViewChild('dataKeyInput') dataKeyInput!: ElementRef<HTMLInputElement>;

  /** Tooltip text for the field */
  tooltip: string = '';
  
  /** The current screen data for duplicate checking */
  @Input() currentScreen: any;
  
  /** The current node's form data for identifying self in duplicate check */
  @Input() formData: any;

  /** Current value of the Data Key */
  value: string = '';
  
  /** Whether the field is in edit mode */
  editMode: boolean = false;
  
  /** Original value stored when entering edit mode */
  originalValue: string = '';
  
  /** Whether the control is disabled */
  disabled: boolean = false;

  /** Whether there is a validation error */
  hasError: boolean = false;

  /** Whether there are unsaved changes */
  get hasChanges(): boolean {
    return this.value !== this.originalValue;
  }

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void;
  private onTouched: () => void;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset edit mode and warning when a different node is selected
    if (changes['formData'] && !changes['formData'].firstChange) {
      const prevKey = changes['formData'].previousValue?.key;
      const currKey = changes['formData'].currentValue?.key;
      
      // Only reset if the node key changed (different node selected)
      if (prevKey !== currKey) {
        this.editMode = false;
      }
    }
  }

  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Handles input value changes
   */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
  }

  /**
   * Enters edit mode and stores the original value.
   */
  enterEditMode(): void {
    this.originalValue = this.value || '';
    this.editMode = true;
    this.hasError = false;
    
    // Focus the input after Angular updates the view
    setTimeout(() => {
      this.dataKeyInput?.nativeElement?.focus();
    });
  }

  /**
   * Cancels edit mode and reverts to the original value.
   */
  cancelEdit(): void {
    this.value = this.originalValue;
    this.editMode = false;
    this.hasError = false;
  }

  /**
   * Validates and saves the changes.
   */
  saveChanges(): void {
    // Only validate if the value changed
    if (this.value !== this.originalValue) {
      // Validate format
      if (!this.screenIdentifierService.isValidIdentifierFormat(this.value)) {
        this.hasError = true;
        this.utilService.showError(this.translate.instant('INVALID_DATA_KEY_FORMAT'));
        return;
      }
      
      // Check for duplicate (pass originalValue to exclude current node)
      if (this.checkDataKeyDuplicate(this.value, this.originalValue)) {
        this.hasError = true;
        this.utilService.showError(this.translate.instant('DUPLICATE_DATA_KEY'));
        return;
      }
      
      // Validation passed - notify parent of change
      this.hasError = false;
      this.onChange(this.value);
      this.onTouched();
    }
    
    this.editMode = false;
  }

  /**
   * Toggles edit mode. Validates the Data Key when exiting edit mode.
   * @deprecated Use enterEditMode(), saveChanges(), or cancelEdit() instead
   */
  toggleEditMode(): void {
    if (this.editMode) {
      this.saveChanges();
    } else {
      this.enterEditMode();
    }
  }

  /**
   * Checks if a Data Key already exists in the current screen tree.
   * Parses the contentAttachment JSON and searches for duplicates.
   * @param dataKey The new Data Key value to check
   * @param originalValue The original Data Key value of the current node (to exclude from check)
   */
  private checkDataKeyDuplicate(dataKey: string, originalValue: string): boolean {
    if (!dataKey) {
      return false;
    }

    // Get the current node's key from formData
    const currentNodeKey = this.formData?.key;

    try {
      // Parse the contentAttachment which contains the entire screen tree as stringified JSON
      if (!this.currentScreen?.contentAttachment) {
        return false;
      }

      const screenTree = JSON.parse(this.currentScreen.contentAttachment);
      if (!screenTree || !Array.isArray(screenTree)) {
        return false;
      }

      return this.findDataKeyInTree(screenTree, dataKey, currentNodeKey, originalValue);
    } catch (error) {
      console.error('[Data Key Editor] Error parsing contentAttachment:', error);
      return false;
    }
  }

  /**
   * Recursively searches for a duplicate Data Key in the screen tree.
   * @param nodes The array of tree nodes to search
   * @param dataKey The Data Key value to check for duplicates
   * @param currentNodeKey The key of the node being edited (to exclude from check)
   * @param originalValue The original Data Key value of the current node (fallback for exclusion)
   */
  private findDataKeyInTree(nodes: any[], dataKey: string, currentNodeKey: string, originalValue: string): boolean {
    if (!nodes || !Array.isArray(nodes)) {
      return false;
    }

    for (const node of nodes) {
      const nodeDataKey = node?.data?.properties?.dataKey;
      
      // Skip the current node being edited
      // Use node.key match OR original dataKey value match (case-sensitive) as fallback
      const isCurrentNode = (currentNodeKey && node.key === currentNodeKey) ||
                            (originalValue && nodeDataKey === originalValue);
      
      if (!isCurrentNode) {
        // Check for duplicate (case-insensitive comparison)
        if (nodeDataKey?.toLowerCase() === dataKey?.toLowerCase()) {
          return true;
        }
      }

      // Recursively check children
      if (node.children && Array.isArray(node.children)) {
        if (this.findDataKeyInTree(node.children, dataKey, currentNodeKey, originalValue)) {
          return true;
        }
      }
    }

    return false;
  }
}
